/**
 * Phase 3 — Petty Cash module pages.
 *
 * Single file exporting 8 sub-pages to keep the bundle and route table simple.
 * Each page is a thin, production-feel surface — dashboard + CRUD + AI scanner.
 */
import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Receipt, ScanLine, CheckSquare, CalendarDays, Building2, AlertTriangle, PieChart,
  Upload, Plus, X, Filter, Loader2, Sparkles, MapPin, IndianRupee, Eye, FileText, Banknote,
  Download, Trash2, RotateCcw, History, Clock,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPost, apiPut, apiDel, api } from '../../lib/api';
import { formatINR } from '../../lib/format';
import { useAuth } from '../../contexts/AuthContext';


// ────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────
const RISK_BADGE = (s) => {
  const n = Number(s || 0);
  if (n >= 61) return ['High', 'bg-rose-100 text-rose-700 border-rose-200'];
  if (n >= 31) return ['Medium', 'bg-amber-100 text-amber-700 border-amber-200'];
  return ['Low', 'bg-emerald-100 text-emerald-700 border-emerald-200'];
};

const STATUS_BADGE = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200',
  processed: 'bg-teal-50 text-teal-700 border-teal-200',
};

// Re-encode large bill photos to JPEG (≤2000px, q0.85) before upload/OCR so we
// never hit the backend size cap and base64 payloads stay small.
async function compressBillImage(file, { maxDim = 2000, quality = 0.85 } = {}) {
  if (!file || !file.type?.startsWith('image/')) return file;
  if (file.type === 'image/jpeg' && file.size < 1_500_000) return file;
  try {
    const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
    const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = dataUrl; });
    const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
    const c = document.createElement('canvas'); c.width = Math.round(img.width * ratio); c.height = Math.round(img.height * ratio);
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    const blob = await new Promise((res) => c.toBlob(res, 'image/jpeg', quality));
    return blob ? new File([blob], (file.name || 'bill').replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' }) : file;
  } catch { return file; }
}

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result || '').split(',')[1] || '');
  r.onerror = reject;
  r.readAsDataURL(file);
});

const KIND_PILL = {
  Material: 'bg-teal-100 text-teal-700',
  Fuel: 'bg-amber-100 text-amber-700',
  Labour: 'bg-violet-100 text-violet-700',
  Transport: 'bg-emerald-100 text-emerald-700',
  'Tools & Equipment': 'bg-cyan-100 text-cyan-700',
  'Site Welfare': 'bg-rose-100 text-rose-700',
  Utilities: 'bg-slate-100 text-slate-700',
  Emergency: 'bg-red-100 text-red-700',
  Miscellaneous: 'bg-zinc-100 text-zinc-700',
};

function Tile({ label, value, sub, accent = 'slate' }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-display">{label}</div>
      <div className={`text-2xl font-bold mt-1 text-${accent}-700`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// 1.1 Dashboard
// ────────────────────────────────────────────────────────────
export function PettyCashDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { apiGet('/petty-cash/dashboard').then(setD).catch(() => {}); }, []);
  if (!d) return <div className="p-8 text-slate-500">Loading…</div>;
  return (
    <div className="p-6 space-y-5">
      <PageHeader title="Petty Cash Dashboard" subtitle="Live KPIs across projects" icon={<IndianRupee size={18} />} />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Tile label="Spent · This Month" value={formatINR(d.month_total)} accent="indigo" />
        <Tile label="Pending Approvals" value={d.pending_count} sub={formatINR(d.pending_value)} accent="amber" />
        <Tile label="Awaiting Finance" value={d.finance_queue_count ?? 0} sub={formatINR(d.finance_queue_value || 0)} accent="indigo" />
        <Tile label="Duplicate Alerts" value={d.duplicate_alerts} accent="rose" />
        <Tile label="High-Risk Employees" value={d.high_risk_employees_count} accent="red" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <h3 className="font-display font-bold text-sm mb-3">Project Spend · This Month</h3>
          <div className="space-y-1.5">
            {(d.project_spend || []).slice(0, 8).map((p, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="truncate max-w-[60%]">{p._id?.slice(0, 8) || '—'}</span>
                <span className="font-bold tabular-nums">{formatINR(p.total)}</span>
              </div>
            ))}
            {!d.project_spend?.length && <div className="text-xs text-slate-400 italic">No spend yet this month.</div>}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <h3 className="font-display font-bold text-sm mb-3">Top 5 Spenders</h3>
          <div className="space-y-1.5">
            {(d.top_spenders || []).map((s, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span>#{i + 1} {s.name || s._id}</span>
                <span className="font-bold tabular-nums">{formatINR(s.total)}</span>
              </div>
            ))}
            {!d.top_spenders?.length && <div className="text-xs text-slate-400 italic">No approved spend yet.</div>}
          </div>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm p-4">
        <h3 className="font-display font-bold text-sm mb-3">Leakage Heatmap by Category</h3>
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
          {(d.leakage_heatmap || []).map((c, i) => (
            <div key={i} className={`px-3 py-2.5 rounded-sm border text-xs ${KIND_PILL[c._id] || 'bg-slate-50'}`}>
              <div className="font-display font-bold uppercase tracking-wider text-[10px]">{c._id}</div>
              <div className="font-bold mt-0.5">{formatINR(c.total)}</div>
              {c.high_risk > 0 && <div className="text-[10px] mt-0.5">🚨 {c.high_risk} high-risk</div>}
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="pc-recent-alerts">
        <h3 className="font-display font-bold text-sm mb-3 flex items-center gap-2"><AlertTriangle size={14} className="text-rose-500" /> Recent Fraud Alerts</h3>
        <ul className="space-y-1.5">
          {(d.recent_alerts || []).slice(0, 6).map((a, i) => (
            <li key={i} className="flex items-start gap-3 text-xs border-b border-slate-100 pb-1.5 last:border-0">
              <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wider ${a.severity === 'high' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>{a.severity}</span>
              <span className="flex-1">{a.explanation}</span>
              <span className="text-slate-400">{new Date(a.created_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}</span>
            </li>
          ))}
          {!d.recent_alerts?.length && <li className="text-xs text-slate-400 italic">No recent alerts</li>}
        </ul>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// 1.2 Expense Request — list + form
// ────────────────────────────────────────────────────────────
export function ExpenseRequest() {
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState({ status: '', category: '' });
  const refresh = () => apiGet('/petty-cash/requests', filter).then(d => setRows(d.items || []));
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title="Expense Request"
        subtitle="Submit and track petty-cash expense claims"
        icon={<Receipt size={18} />}
        actions={
          <button onClick={() => setShowForm(true)} className="px-3 py-1.5 bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm text-xs font-display font-bold flex items-center gap-1.5" data-testid="pc-new-expense-btn">
            <Plus size={14} /> New Expense
          </button>
        }
      />
      <div className="flex items-center gap-2">
        <select value={filter.status} onChange={(e) => setFilter(f => ({ ...f, status: e.target.value }))} className="px-2 py-1 border border-slate-300 rounded-sm text-xs">
          <option value="">All statuses</option><option value="pending">Pending</option><option value="approved">Approved</option><option value="processed">Processed</option><option value="rejected">Rejected</option>
        </select>
        <select value={filter.category} onChange={(e) => setFilter(f => ({ ...f, category: e.target.value }))} className="px-2 py-1 border border-slate-300 rounded-sm text-xs">
          <option value="">All categories</option>
          {Object.keys(KIND_PILL).map(k => <option key={k} value={k}>{k}</option>)}
        </select>
        <span className="text-xs text-slate-500 ml-auto">{rows.length} record(s)</span>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="pc-expense-table">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-display">
            <tr>
              <th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Project</th>
              <th className="text-left px-3 py-2">Category</th><th className="text-left px-3 py-2">Vendor</th>
              <th className="text-right px-3 py-2">Amount</th><th className="text-left px-3 py-2">By</th>
              <th className="text-center px-3 py-2">Risk</th><th className="text-center px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const [rb, rc] = RISK_BADGE(r.ai_risk_score);
              return (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 text-xs">{r.expense_date}</td>
                  <td className="px-3 py-2 text-xs truncate max-w-[180px]">{r.project_name}</td>
                  <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold ${KIND_PILL[r.category] || 'bg-slate-100'}`}>{r.category}</span></td>
                  <td className="px-3 py-2 text-xs">{r.vendor_name}</td>
                  <td className="px-3 py-2 text-xs text-right font-bold tabular-nums">{formatINR(r.actual_amount)}</td>
                  <td className="px-3 py-2 text-xs">{r.submitted_by_name}</td>
                  <td className="px-3 py-2 text-center"><span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold border ${rc}`}>{rb} {r.ai_risk_score || 0}</span></td>
                  <td className="px-3 py-2 text-center"><span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${STATUS_BADGE[r.status] || 'bg-slate-100'}`}>{r.status}</span></td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan="8" className="text-center text-slate-400 italic py-6 text-sm">No expense requests match the filters.</td></tr>}
          </tbody>
        </table>
      </div>
      {showForm && <ExpenseFormModal onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); refresh(); }} />}
    </div>
  );
}


function ExpenseFormModal({ onClose, onSaved }) {
  const [assignees, setAssignees] = useState({ offices: [] });
  const [categories, setCategories] = useState([]);
  const [openAdvances, setOpenAdvances] = useState([]);
  const [form, setForm] = useState({
    cost_centre_kind: 'office',
    office_location_id: '',
    category: '', sub_category: '', actual_amount: 0,
    invoice_number: null, gst_number: null,
    reason: '', priority: 'Routine', vendor_name: '', payment_mode: 'Cash',
    expense_date: new Date().toISOString().slice(0, 10),
    cash_advance_id: '',
  });
  const [submitting, setSubmitting] = useState(false);
  // Bill photo + OCR auto-fill
  const [bill, setBill] = useState(null);
  const [ocr, setOcr] = useState(null); // null | 'reading' | 'failed' | {extracted}
  const [dupReason, setDupReason] = useState(null);
  const billRef = useRef(null);

  const onPickBill = async (e) => {
    const raw = e.target.files?.[0];
    if (!raw) return;
    e.target.value = '';
    const compressed = await compressBillImage(raw);
    setBill(compressed);
    setDupReason(null);
    setOcr('reading');
    setForm(f => ({ ...f, invoice_number: null, gst_number: null }));
    try {
      const fd = new FormData();
      fd.append('file', compressed, compressed.name || 'bill.jpg');
      const { data } = await api.post('/petty-cash/extract-bill', fd, { timeout: 120000 });
      const ex = data?.extracted || {};
      setForm(f => ({
        ...f,
        vendor_name: ex.vendor_name || f.vendor_name,
        actual_amount: Number(ex.total_amount) > 0 ? Number(ex.total_amount) : f.actual_amount,
        invoice_number: ex.invoice_number || null,
        gst_number: ex.gst_number || null,
        // expense_date may only be today or yesterday (backend rule) — adopt
        // the bill date only when it falls inside that window.
        expense_date: (() => {
          if (!ex.invoice_date) return f.expense_date;
          const diff = (new Date().setHours(0, 0, 0, 0) - new Date(ex.invoice_date).setHours(0, 0, 0, 0)) / 86400000;
          return diff >= 0 && diff <= 1 ? ex.invoice_date : f.expense_date;
        })(),
      }));
      setOcr(ex);
      if (data?.is_duplicate) {
        setDupReason(data.duplicate_reason || 'Duplicate bill detected');
        toast.error('Duplicate bill — already in the system');
      } else {
        toast.success('Bill details auto-filled from the photo');
      }
    } catch {
      setOcr('failed');
    }
  };
  useEffect(() => {
    apiGet('/petty-cash/assignees').then((r) => setAssignees({
      offices: Array.isArray(r?.offices) ? r.offices : [],
    })).catch(() => {});
    apiGet('/petty-cash/categories').then((r) => {
      const cats = Array.isArray(r?.items) ? r.items : [];
      setCategories(cats);
      if (cats.length) setForm((f) => f.category ? f : ({ ...f, category: cats[0].name }));
    }).catch(() => {});
    // Load my open cash advances so this bill can optionally be charged to one
    apiGet('/cash-advances', { outstanding_only: true }).then((r) => {
      setOpenAdvances(Array.isArray(r?.items) ? r.items : []);
    }).catch(() => {});
  }, []);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (form.reason.length < 20) return toast.error('Reason must be at least 20 characters');
    if (!form.office_location_id) return toast.error('Pick an office location');
    if (!form.vendor_name) return toast.error('Vendor name required');
    if (!form.category) return toast.error('Pick a category');
    if (dupReason) return toast.error('Duplicate bill — submission blocked');
    setSubmitting(true);
    try {
      const payload = { ...form, actual_amount: Number(form.actual_amount || 0) };
      if (!payload.cash_advance_id) delete payload.cash_advance_id;
      if (bill) {
        try {
          payload.bill_image_base64 = await fileToBase64(bill);
          payload.bill_image_mime = bill.type || 'image/jpeg';
        } catch { /* submit without the image */ }
      }
      await apiPost('/petty-cash/requests', payload);
      toast.success('Expense submitted');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-end md:items-center justify-center p-3">
      <div className="bg-white rounded-md w-full max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="pc-expense-form">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white">
          <h3 className="font-display font-bold text-lg">New Expense Request</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Bill Photo · details auto-fill via OCR">
            <input ref={billRef} hidden type="file" accept="image/*,application/pdf" onChange={onPickBill} data-testid="pc-form-bill-input" />
            <button type="button" onClick={() => billRef.current?.click()} className="w-full border-2 border-dashed border-slate-300 hover:border-[#1F7A6E] rounded-sm p-4 text-center" data-testid="pc-form-bill-btn">
              <Upload size={18} className="mx-auto text-slate-400 mb-1" />
              <div className="text-xs font-display font-bold">{bill ? bill.name : 'Upload / scan the bill — vendor, amount & bill no. auto-fill'}</div>
            </button>
            {ocr === 'reading' && (
              <div className="mt-2 flex items-center gap-2 text-xs text-teal-700"><Loader2 size={12} className="animate-spin" /> Reading bill details…</div>
            )}
            {ocr === 'failed' && (
              <div className="mt-2 text-xs text-amber-700">Couldn't read the bill — fill the details manually.</div>
            )}
            {ocr && typeof ocr === 'object' && (
              <div className="mt-2 text-xs text-emerald-700" data-testid="pc-form-ocr-summary">
                Read from bill: {ocr.vendor_name || '—'} · ₹{ocr.total_amount ?? '—'} · Bill #{ocr.invoice_number || '—'}
              </div>
            )}
            {dupReason && (
              <div className="mt-2 flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-sm p-2 text-xs text-rose-700" data-testid="pc-form-duplicate-banner">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" /> {dupReason}
              </div>
            )}
          </Field>
          <Field label="Cost Centre">
            <select value={form.office_location_id} onChange={(e) => set('office_location_id', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="pc-form-office">
              <option value="">Select office…</option>
              {assignees.offices.map(o => <option key={`${o.source || 'cm'}-${o.id}`} value={o.id}>{o.name}{o.city ? ` · ${o.city}` : ''}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Category">
              <select value={form.category} onChange={(e) => set('category', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="pc-form-category">
                <option value="">Select…</option>
                {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Actual Amount (₹)">
              <input type="number" value={form.actual_amount} onChange={(e) => set('actual_amount', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="pc-form-amount" />
            </Field>
            <Field label="Priority">
              <select value={form.priority} onChange={(e) => set('priority', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm">
                <option>Routine</option><option>Urgent</option><option>Emergency</option>
              </select>
            </Field>
            <Field label="Payment Mode">
              <select value={form.payment_mode} onChange={(e) => set('payment_mode', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm">
                <option>Cash</option><option>UPI</option><option>Vendor Wallet</option><option>Fuel Card</option><option>Bank Transfer</option>
              </select>
            </Field>
            <Field label="Vendor Name">
              <input value={form.vendor_name} onChange={(e) => set('vendor_name', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            </Field>
            <Field label="Expense Date">
              <input type="date" value={form.expense_date} onChange={(e) => set('expense_date', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            </Field>
          </div>
          {openAdvances.length > 0 && (
            <Field label="Charge to Open Cash Advance (optional)">
              <select
                value={form.cash_advance_id}
                onChange={(e) => set('cash_advance_id', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                data-testid="pc-form-cash-advance"
              >
                <option value="">— Don&apos;t link to any advance —</option>
                {openAdvances.map(a => (
                  <option key={a.id} value={a.id}>
                    {a.advance_no} · {a.employee_name} · Outstanding {formatINR(a.outstanding_amount)}
                  </option>
                ))}
              </select>
              <div className="text-[11px] text-slate-500 mt-1">
                When this bill is approved, its amount will reduce the outstanding on the linked advance.
              </div>
            </Field>
          )}
          <Field label={`Reason (min 20 chars · ${form.reason.length}/20)`}>
            <textarea rows={3} value={form.reason} onChange={(e) => set('reason', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="pc-form-reason" />
          </Field>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200">
          <button onClick={onClose} className="px-3 py-1.5 text-sm">Cancel</button>
          <button disabled={submitting || !!dupReason} onClick={save} className="px-4 py-1.5 bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm text-sm font-display font-bold disabled:opacity-50" data-testid="pc-form-submit">
            {submitting ? 'Saving…' : 'Submit Expense'}
          </button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest font-display text-slate-500">{label}</label>
    {children}
  </div>
);


// ────────────────────────────────────────────────────────────
// 1.3 Bill Scanner (AI)
// ────────────────────────────────────────────────────────────
export function BillScanner() {
  const [file, setFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const scan = async () => {
    if (!file) return toast.error('Pick a bill image first');
    setScanning(true);
    setResult(null);
    try {
      // Re-encode large images to a sane JPEG before upload so we never
      // trip the 8 MB backend cap (was a hidden cause of "AI scan failed").
      let payload = file;
      if (file.type?.startsWith('image/') && file.size > 1_500_000) {
        payload = await (async () => {
          try {
            const r = new FileReader();
            const dataUrl = await new Promise((resolve, reject) => { r.onload = () => resolve(r.result); r.onerror = reject; r.readAsDataURL(file); });
            const img = await new Promise((resolve, reject) => { const i = new Image(); i.onload = () => resolve(i); i.onerror = reject; i.src = dataUrl; });
            const ratio = Math.min(1, 2000 / Math.max(img.width, img.height));
            const c = document.createElement('canvas'); c.width = img.width * ratio; c.height = img.height * ratio;
            c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
            const blob = await new Promise(res => c.toBlob(res, 'image/jpeg', 0.85));
            return blob ? new File([blob], (file.name || 'bill').replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' }) : file;
          } catch { return file; }
        })();
      }
      const fd = new FormData();
      fd.append('file', payload, payload.name || 'bill.jpg');
      const { data } = await api.post('/petty-cash/scan-bill', fd, { timeout: 120000 });
      setResult(data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'AI scan failed';
      toast.error(typeof detail === 'string' ? detail : 'AI scan failed');
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <PageHeader title="Bill Scanner · AI" subtitle="Ellora AI vision OCR + 8 validation checks → risk score" icon={<ScanLine size={18} />} />
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div
          className="border-2 border-dashed border-slate-300 hover:border-[#1F7A6E] rounded-sm p-8 text-center cursor-pointer"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); setFile(e.dataTransfer.files?.[0]); }}
          data-testid="pc-scanner-dropzone"
        >
          <input ref={inputRef} hidden type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0])} data-testid="pc-scanner-file" />
          <Upload size={26} className="mx-auto text-slate-400 mb-2" />
          <div className="font-display font-bold text-sm">{file ? file.name : 'Drop bill image (JPG / PNG / PDF), or click to browse'}</div>
          <div className="text-[11px] text-slate-500 mt-1">Max 8 MB</div>
        </div>
        <button
          onClick={scan} disabled={!file || scanning}
          className="mt-4 px-4 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm text-sm font-display font-bold flex items-center gap-2 disabled:opacity-50"
          data-testid="pc-scanner-scan-btn"
        >
          {scanning ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {scanning ? 'AI extracting…' : 'Run AI Scan'}
        </button>
      </div>

      {result && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="pc-scanner-result">
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <h3 className="font-display font-bold text-sm mb-3 flex items-center justify-between">
              Extracted Fields
              <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold border ${RISK_BADGE(result.risk_score)[1]}`}>Risk {result.risk_score} · {result.risk_band}</span>
            </h3>
            <dl className="space-y-1 text-xs">
              {[['Vendor', 'vendor_name'], ['GST', 'gst_number'], ['Invoice #', 'invoice_number'], ['Invoice date', 'invoice_date'], ['Total ₹', 'total_amount'], ['Tax ₹', 'tax_amount'], ['Payment terms', 'payment_terms']].map(([lab, key]) => (
                <div key={key} className="flex border-b border-slate-100 pb-1">
                  <dt className="w-32 text-slate-500">{lab}</dt>
                  <dd className="flex-1 font-mono">{result.extracted?.[key] ?? '—'}</dd>
                </div>
              ))}
            </dl>
            {result.extracted?.line_items?.length > 0 && (
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-widest font-display text-slate-500 mb-1">Line items</div>
                <table className="w-full text-xs border border-slate-200">
                  <thead className="bg-slate-50"><tr><th className="text-left px-2 py-1">Item</th><th className="text-right px-2 py-1">Qty</th><th className="text-right px-2 py-1">Rate</th><th className="text-right px-2 py-1">Amount</th></tr></thead>
                  <tbody>
                    {result.extracted.line_items.map((it, i) => (
                      <tr key={i} className="border-t border-slate-100"><td className="px-2 py-1">{it.item || it.name}</td><td className="px-2 py-1 text-right">{it.quantity || it.qty}</td><td className="px-2 py-1 text-right">{it.rate}</td><td className="px-2 py-1 text-right">{it.amount}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <h3 className="font-display font-bold text-sm mb-3">AI Validation Checks</h3>
            <ul className="space-y-1.5">
              {(result.validation || []).map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-xs border-b border-slate-100 pb-1.5 last:border-0">
                  <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wider shrink-0 ${
                    c.status === 'pass' ? 'bg-emerald-100 text-emerald-700' :
                    c.status === 'warning' ? 'bg-amber-100 text-amber-700' :
                    'bg-rose-100 text-rose-700'
                  }`}>{c.status}</span>
                  <div className="flex-1">
                    <div className="font-bold">{c.name}</div>
                    <div className="text-slate-500">{c.detail}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// 1.4 Approvals
// ────────────────────────────────────────────────────────────
// Bill Detail Drawer — shows full metadata + audit log timeline
// ────────────────────────────────────────────────────────────
const AUDIT_TONE = {
  submitted: 'bg-teal-50 text-teal-700 border-teal-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200',
  under_review: 'bg-teal-50 text-teal-700 border-teal-200',
  returned_for_correction: 'bg-amber-50 text-amber-700 border-amber-200',
  resubmitted: 'bg-purple-50 text-purple-700 border-purple-200',
  request_change: 'bg-orange-50 text-orange-700 border-orange-200',
  soft_deleted: 'bg-slate-100 text-slate-700 border-slate-300',
};

const AUDIT_LABEL = {
  submitted: 'Submitted', approved: 'Approved', rejected: 'Rejected',
  under_review: 'Under Review', returned_for_correction: 'Returned for Correction',
  resubmitted: 'Resubmitted', request_change: 'Change Requested',
  soft_deleted: 'Soft-Deleted',
};

function BillDetailDrawer({ bill, open, onClose, onAction }) {
  const { user } = useAuth();
  const isSuperAdmin = (user?.role || '').toLowerCase() === 'super_admin';
  if (!open || !bill) return null;
  const audit = bill.audit_log || [];

  const ask = async (label, endpoint, requiresRemarks, prompt) => {
    let remarks;
    if (requiresRemarks) {
      remarks = window.prompt(prompt);
      if (!remarks || remarks.trim().length < 4) return;
    }
    try {
      const body = requiresRemarks ? { remarks: remarks.trim() } : {};
      await apiPut(`/petty-cash/requests/${bill.id}/${endpoint}`, body);
      toast.success(`${label} done`);
      onAction();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${label} failed`);
    }
  };

  const softDelete = async () => {
    if (!window.confirm(`Soft-delete this bill (${bill.vendor_name} · ${formatINR(bill.actual_amount)})?\nIt will be hidden from default lists; super_admin can still recover it.`)) return;
    try {
      await apiDel(`/petty-cash/requests/${bill.id}`);
      toast.success('Bill soft-deleted');
      onAction();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="bill-detail-drawer">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-display font-bold">Petty Cash Bill</div>
            <div className="font-display font-bold text-base text-[#1F7A6E]">{bill.vendor_name} · {formatINR(bill.actual_amount)}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-sm"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          {bill.invoice_image_url && (
            <img src={bill.invoice_image_url} alt="bill" className="w-full max-h-72 object-contain border border-slate-200 rounded-sm" />
          )}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Status</div><div className="font-medium">{bill.status}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Category</div><div className="font-medium">{bill.category}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Bill #</div><div className="font-medium">{bill.invoice_number || '—'}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Date</div><div className="font-medium">{bill.expense_date}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Project</div><div className="font-medium truncate">{bill.project_name || '—'}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Submitted by</div><div className="font-medium truncate">{bill.created_by || bill.submitted_by_name || '—'}</div></div>
          </div>
          {bill.reason && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Reason</div>
              <div className="text-sm text-slate-700">{bill.reason}</div>
            </div>
          )}
          {bill.return_remarks && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-sm">
              <div className="text-[10px] uppercase tracking-wider text-amber-700 mb-1 font-bold">Correction Remarks</div>
              <div className="text-amber-900">{bill.return_remarks}</div>
            </div>
          )}

          {/* Activity Timeline */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 font-bold flex items-center gap-1"><History size={11} /> Activity Timeline</div>
            <div className="space-y-2">
              {audit.length === 0 && <div className="text-xs text-slate-400 italic">No history yet.</div>}
              {audit.map((e, i) => (
                <div key={e.id || i} className="flex items-start gap-2 text-sm" data-testid={`audit-row-${e.event}`}>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded-sm ${AUDIT_TONE[e.event] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                    {AUDIT_LABEL[e.event] || e.event}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-slate-700">{e.actor_name || '—'} · <span className="text-slate-400 text-[11px]"><Clock size={9} className="inline" /> {new Date(e.at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span></div>
                    {e.remarks && <div className="text-xs text-slate-500 mt-0.5">&quot;{e.remarks}&quot;</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="border-t border-slate-200 pt-4 flex flex-wrap gap-2">
            {(bill.status === 'pending' || bill.status === 'Submitted') && (
              <button data-testid="action-under-review" onClick={() => ask('Mark Under Review', 'under-review', false)}
                className="px-3 py-1.5 text-xs bg-teal-600 hover:bg-teal-700 text-white rounded-sm">
                Mark Under Review
              </button>
            )}
            {(['pending', 'Submitted', 'Under Review'].includes(bill.status)) && (
              <>
                <button data-testid="action-approve" onClick={() => ask('Approve', 'approve', false)}
                  className="px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">Approve</button>
                <button data-testid="action-return" onClick={() => ask('Return for Correction', 'return-for-correction', true, 'What needs correction? (mandatory, min 4 chars)')}
                  className="px-3 py-1.5 text-xs bg-amber-600 hover:bg-amber-700 text-white rounded-sm flex items-center gap-1">
                  <RotateCcw size={11} /> Return for Correction
                </button>
                <button data-testid="action-reject" onClick={() => {
                  const r = window.prompt('Reason for rejection (min 4 chars)');
                  if (!r || r.length < 4) return;
                  apiPut(`/petty-cash/requests/${bill.id}/reject?reason=${encodeURIComponent(r)}`)
                    .then(() => { toast.success('Rejected'); onAction(); onClose(); })
                    .catch(e => toast.error(e?.response?.data?.detail || 'Reject failed'));
                }} className="px-3 py-1.5 text-xs text-rose-700 border border-rose-200 hover:bg-rose-50 rounded-sm">Reject</button>
              </>
            )}
            {bill.status === 'approved' && (
              <button data-testid="action-request-change" onClick={() => ask('Request Change', 'request-change', true, 'Why does this approved bill need a change? (mandatory, min 4 chars)')}
                className="px-3 py-1.5 text-xs bg-orange-600 hover:bg-orange-700 text-white rounded-sm">
                Request Change on Approved Bill
              </button>
            )}
            {isSuperAdmin && !bill.is_deleted && (
              <button data-testid="action-soft-delete" onClick={softDelete}
                className="ml-auto px-3 py-1.5 text-xs text-rose-700 border border-rose-300 hover:bg-rose-50 rounded-sm flex items-center gap-1">
                <Trash2 size={11} /> Soft-Delete
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// 1.4 Approvals Queue — bulk approve + new states + detail drawer
// ────────────────────────────────────────────────────────────
export function ApprovalsQueue() {
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState(() => new Set());
  const [selected, setSelected] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [bulking, setBulking] = useState(false);

  const refresh = () => apiGet('/petty-cash/requests', { status: 'pending', limit: 200 }).then(d => setRows(d.items || []));
  useEffect(() => { refresh(); }, []);

  const togglePick = (id) => {
    const n = new Set(picked);
    if (n.has(id)) n.delete(id); else n.add(id);
    setPicked(n);
  };
  const pickAll = () => setPicked(new Set(rows.map(r => r.id)));
  const pickNone = () => setPicked(new Set());

  const bulkApprove = async () => {
    if (picked.size === 0) return toast.error('Select at least one bill');
    if (!window.confirm(`Approve ${picked.size} bill(s) in bulk?`)) return;
    setBulking(true);
    try {
      const r = await apiPut('/petty-cash/requests/bulk-approve', { ids: Array.from(picked), note: 'Bulk approval' });
      toast.success(`Approved ${r.approved_count} · Skipped ${r.skipped_count}`);
      setPicked(new Set());
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk approve failed');
    }
    setBulking(false);
  };

  const exportData = async (format) => {
    setExporting(true);
    try {
      const resp = await api.post('/petty-cash/export', { format }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `petty-cash-${new Date().toISOString().slice(0,10)}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Export ${format} failed`);
    }
    setExporting(false);
  };

  const openBill = async (id) => {
    try {
      const full = await apiGet(`/petty-cash/requests/${id}`);
      setSelected(full);
    } catch (e) {
      // Fall back to whatever's on the row
      setSelected(rows.find(r => r.id === id) || null);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <PageHeader title="Pending Approvals" subtitle={`${rows.length} expense(s) awaiting decision`} icon={<CheckSquare size={18} />} />

      {/* Bulk + Export actions toolbar */}
      <div className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-sm p-2">
        <button onClick={pickAll} className="text-xs text-slate-600 hover:text-[#1F7A6E]" data-testid="pc-pick-all">Select all</button>
        <button onClick={pickNone} className="text-xs text-slate-600 hover:text-[#1F7A6E]" data-testid="pc-pick-none">Clear</button>
        <span className="text-xs text-slate-400">·</span>
        <span className="text-xs text-slate-600" data-testid="pc-picked-count">{picked.size} selected</span>
        <button onClick={bulkApprove} disabled={bulking || picked.size === 0}
          className="px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm disabled:opacity-50 flex items-center gap-1"
          data-testid="pc-bulk-approve">
          {bulking ? <Loader2 className="animate-spin" size={11} /> : <CheckSquare size={11} />}
          Bulk Approve
        </button>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => exportData('excel')} disabled={exporting}
            className="px-3 py-1.5 text-xs border border-slate-300 hover:bg-slate-50 rounded-sm flex items-center gap-1"
            data-testid="pc-export-excel">
            <Download size={11} /> Excel
          </button>
          <button onClick={() => exportData('pdf')} disabled={exporting}
            className="px-3 py-1.5 text-xs border border-slate-300 hover:bg-slate-50 rounded-sm flex items-center gap-1"
            data-testid="pc-export-pdf">
            <Download size={11} /> PDF
          </button>
        </div>
      </div>

      <div className="space-y-2" data-testid="pc-approvals-list">
        {rows.map(r => {
          const [rb, rc] = RISK_BADGE(r.ai_risk_score);
          const isPicked = picked.has(r.id);
          return (
            <div key={r.id} className={`bg-white border rounded-sm p-4 flex flex-col md:flex-row md:items-center gap-3 ${isPicked ? 'border-[#1F7A6E] ring-1 ring-[#1F7A6E]/30' : 'border-slate-200'}`}>
              <input type="checkbox" checked={isPicked} onChange={() => togglePick(r.id)}
                className="w-4 h-4 mt-1 md:mt-0" data-testid={`pc-pick-${r.id}`} />
              <InvoiceThumb url={r.invoice_image_url} alt={`Bill from ${r.vendor_name || 'vendor'}`} />
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openBill(r.id)} data-testid={`pc-row-open-${r.id}`}>
                <div className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold ${KIND_PILL[r.category]}`}>{r.category}</span>
                  <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold border ${rc}`}>{rb} {r.ai_risk_score || 0}</span>
                  <span className="text-[10px] text-slate-500">{r.priority}</span>
                </div>
                <div className="font-display font-bold mt-1 truncate">{r.vendor_name} · {formatINR(r.actual_amount)}</div>
                <div className="text-xs text-slate-500">{r.project_name} · {r.submitted_by_name}</div>
                <div className="text-xs text-slate-600 mt-1">{r.reason}</div>
              </div>
              <button onClick={() => openBill(r.id)} className="px-3 py-1.5 text-xs border border-slate-300 hover:bg-slate-50 rounded-sm flex items-center gap-1" data-testid={`pc-view-${r.id}`}>
                <Eye size={11} /> View
              </button>
            </div>
          );
        })}
        {!rows.length && <div className="text-center text-slate-400 italic py-8 text-sm">Nothing in the queue. Nice and clean.</div>}
      </div>

      <BillDetailDrawer bill={selected} open={!!selected} onClose={() => setSelected(null)} onAction={refresh} />
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// 1.5 Daily Cash Closure
// ────────────────────────────────────────────────────────────
export function DailyCashClosure() {
  const [rows, setRows] = useState([]);
  useEffect(() => { apiGet('/petty-cash/closures').then(d => setRows(d.items || [])); }, []);
  return (
    <div className="p-6 space-y-4">
      <PageHeader title="Daily Cash Closure" subtitle="End-of-day reconciliation per project" icon={<CalendarDays size={18} />} />
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-display">
            <tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Project</th>
              <th className="px-3 py-2 text-right">Opening</th><th className="px-3 py-2 text-right">Expenses</th>
              <th className="px-3 py-2 text-right">Cash-in-hand</th><th className="px-3 py-2 text-right">Variance</th>
              <th className="px-3 py-2 text-center">Status</th><th className="px-3 py-2 text-left">By</th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-3 py-2 text-xs">{r.closure_date}</td>
                <td className="px-3 py-2 text-xs truncate max-w-[200px]">{r.project_name}</td>
                <td className="px-3 py-2 text-xs text-right tabular-nums">{formatINR(r.opening_balance)}</td>
                <td className="px-3 py-2 text-xs text-right tabular-nums">{formatINR(r.expenses_total)}</td>
                <td className="px-3 py-2 text-xs text-right tabular-nums">{formatINR(r.cash_in_hand)}</td>
                <td className={`px-3 py-2 text-xs text-right tabular-nums font-bold ${Math.abs(r.variance) > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{formatINR(r.variance)}</td>
                <td className="px-3 py-2 text-center"><span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${r.status === 'balanced' ? 'bg-emerald-100 text-emerald-700' : r.status === 'escalated' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>{r.status}</span></td>
                <td className="px-3 py-2 text-xs">{r.submitted_by}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan="8" className="text-center text-slate-400 italic py-6 text-sm">No closures recorded yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// 1.6 Vendor Intelligence
// ────────────────────────────────────────────────────────────
export function VendorIntelligence() {
  const [rows, setRows] = useState([]);
  useEffect(() => { apiGet('/petty-cash/vendors').then(d => setRows(d.items || [])); }, []);
  return (
    <div className="p-6 space-y-4">
      <PageHeader title="Vendor Intelligence" subtitle="Spend concentration + AI risk flags per vendor" icon={<Building2 size={18} />} />
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-display">
            <tr><th className="px-3 py-2 text-left">Vendor</th><th className="px-3 py-2 text-right">Txns</th>
              <th className="px-3 py-2 text-right">Total ₹</th><th className="px-3 py-2 text-right">Projects</th>
              <th className="px-3 py-2 text-right">Approval %</th><th className="px-3 py-2 text-center">Risk</th>
              <th className="px-3 py-2 text-left">AI Flags</th></tr>
          </thead>
          <tbody data-testid="pc-vendors-table">
            {rows.map(v => (
              <tr key={v.vendor_name} className="border-t border-slate-100">
                <td className="px-3 py-2 font-display font-semibold">{v.vendor_name}</td>
                <td className="px-3 py-2 text-right tabular-nums">{v.txns}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatINR(v.total_value)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{v.projects_count}</td>
                <td className="px-3 py-2 text-right tabular-nums">{v.approval_rate}%</td>
                <td className="px-3 py-2 text-center"><span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase ${v.risk === 'high' ? 'bg-rose-100 text-rose-700' : v.risk === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>{v.risk}</span></td>
                <td className="px-3 py-2 text-xs">{v.flags?.join(' · ') || <span className="text-slate-400 italic">—</span>}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan="7" className="text-center text-slate-400 italic py-6 text-sm">No vendor data yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// 1.7 Fraud Alerts
// ────────────────────────────────────────────────────────────
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function InvoiceThumb({ url, alt }) {
  // Iter 141 — invoice viewer for the Fraud Alerts feed. The image is served
  // through an auth-gated endpoint, so we fetch it as a blob with the user's
  // bearer token, then turn it into an object URL the <img> tag can render.
  const [src, setSrc] = useState(null);
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let revoke = null;
    let cancelled = false;
    if (!url) { setFailed(true); return; }
    (async () => {
      try {
        const r = await api.get(url.startsWith('/api') ? url.replace(/^\/api/, '') : url, { responseType: 'blob' });
        if (cancelled) return;
        const obj = URL.createObjectURL(r.data);
        revoke = obj;
        setSrc(obj);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; if (revoke) URL.revokeObjectURL(revoke); };
  }, [url]);
  if (failed) {
    return (
      <div className="w-16 h-16 rounded-sm border border-slate-200 bg-slate-50 text-[9px] text-slate-400 flex items-center justify-center text-center px-1">
        no bill
      </div>
    );
  }
  if (!src) {
    return (
      <div className="w-16 h-16 rounded-sm border border-slate-200 bg-slate-50 flex items-center justify-center">
        <Loader2 size={14} className="animate-spin text-slate-300" />
      </div>
    );
  }
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="block w-16 h-16 rounded-sm overflow-hidden border border-slate-200 hover:border-[#1F7A6E] focus:outline-none focus:ring-2 focus:ring-[#1F7A6E] bg-slate-50"
        title="Click to enlarge the bill"
        data-testid="alert-invoice-thumb"
      >
        <img src={src} alt={alt || 'Invoice'} className="w-full h-full object-cover" />
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setOpen(false)}
          data-testid="alert-invoice-modal"
        >
          <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute -top-3 -right-3 bg-white text-slate-800 rounded-full p-1.5 shadow hover:bg-slate-100"
              data-testid="alert-invoice-modal-close"
              aria-label="Close invoice viewer"
            >
              <X size={16} />
            </button>
            <img
              src={src}
              alt={alt || 'Invoice'}
              className="w-full max-h-[88vh] object-contain rounded-sm bg-white"
            />
            <a
              href={src}
              download
              className="absolute bottom-3 right-3 bg-white/95 text-slate-800 text-[11px] font-display font-bold uppercase tracking-wide px-2 py-1 rounded-sm shadow hover:bg-white"
            >
              Download
            </a>
          </div>
        </div>
      )}
    </>
  );
}

export function FraudAlerts() {
  const [rows, setRows] = useState([]);
  const refresh = () => apiGet('/petty-cash/alerts').then(d => setRows(d.items || []));
  useEffect(() => { refresh(); }, []);
  const update = async (id, status) => { try { await apiPut(`/petty-cash/alerts/${id}`, { status }); toast.success('Updated'); refresh(); } catch { toast.error('Failed'); } };
  return (
    <div className="p-6 space-y-4">
      <PageHeader title="Fraud Alerts" subtitle="AI-generated red flags awaiting review" icon={<AlertTriangle size={18} />} />
      <div className="space-y-2" data-testid="pc-alerts-list">
        {rows.map(a => (
          <div key={a.id} className="bg-white border border-slate-200 rounded-sm p-3 flex items-start gap-3">
            {/* Iter 141 — invoice thumbnail (click to enlarge). When no bill
                is on file the component renders a small placeholder. */}
            <InvoiceThumb url={a.invoice_image_url} alt={`Invoice for ${a.type.replaceAll('_', ' ')}`} />
            <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wider ${a.severity === 'high' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>{a.severity}</span>
            <div className="flex-1">
              <div className="font-display font-bold text-sm">{a.type.replaceAll('_', ' ')}</div>
              <div className="text-xs text-slate-600">{a.explanation}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">{new Date(a.created_at).toLocaleString('en-IN')} · AI confidence {a.ai_confidence}%</div>
            </div>
            <div className="flex items-center gap-1.5">
              <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold ${a.status === 'Resolved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{a.status}</span>
              {a.status !== 'Resolved' && (
                <button onClick={() => update(a.id, 'Resolved')} className="text-[10px] px-2 py-0.5 bg-emerald-600 text-white rounded-sm hover:bg-emerald-700" data-testid="petty-cash-fa-resolve">Resolve</button>
              )}
            </div>
          </div>
        ))}
        {!rows.length && <div className="text-center text-slate-400 italic py-8 text-sm">No alerts firing — your books are clean.</div>}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// 1.8 Reports — Phase 3C (PDF + Excel exports)
// ────────────────────────────────────────────────────────────
export function PettyCashReports() {
  const [items, setItems] = useState([]);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState(null);
  useEffect(() => { apiGet('/petty-cash/reports').then(d => setItems(d.items || [])).catch(() => {}); }, []);

  const download = async (id, fmt) => {
    const key = `${id}-${fmt}`;
    setBusy(key);
    try {
      const resp = await api.get(`/petty-cash/reports/${id}/export`, {
        params: { format: fmt, month },
        responseType: 'blob',
        timeout: 60000,
      });
      const blob = new Blob([resp.data], { type: resp.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${id}_${month}.${fmt}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <PageHeader title="Petty Cash Reports" subtitle="Exportable PDF / Excel" icon={<PieChart size={18} />} />
      <div className="bg-white border border-slate-200 rounded-sm p-3 flex items-center gap-3">
        <label className="text-[10px] uppercase tracking-widest font-display text-slate-500">Period</label>
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="px-3 py-1.5 border border-slate-300 rounded-sm text-sm"
          data-testid="pc-reports-month"
        />
        <div className="text-xs text-slate-500 ml-auto">All projects · {items.length} reports available</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="pc-reports-grid">
        {items.map((r) => (
          <div key={r.id} className="bg-white border border-slate-200 rounded-sm p-4 flex flex-col">
            <FileText size={18} className="text-[#1F7A6E] mb-2" />
            <div className="font-display font-bold text-sm">{r.title}</div>
            <div className="text-[10px] text-slate-400 mt-1 flex-1">Period: {month}</div>
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={() => download(r.id, 'pdf')}
                disabled={busy === `${r.id}-pdf`}
                className="flex-1 px-2 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm disabled:opacity-50 flex items-center justify-center gap-1.5"
                data-testid={`pc-report-${r.id}-pdf`}
              >
                {busy === `${r.id}-pdf` ? <Loader2 size={11} className="animate-spin" /> : null} PDF
              </button>
              <button
                onClick={() => download(r.id, 'xlsx')}
                disabled={busy === `${r.id}-xlsx`}
                className="flex-1 px-2 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider border border-[#1F7A6E] text-[#1F7A6E] hover:bg-[#1F7A6E] hover:text-white rounded-sm disabled:opacity-50 flex items-center justify-center gap-1.5"
                data-testid={`pc-report-${r.id}-xlsx`}
              >
                {busy === `${r.id}-xlsx` ? <Loader2 size={11} className="animate-spin" /> : null} Excel
              </button>
            </div>
          </div>
        ))}
        {!items.length && <div className="col-span-full text-center text-slate-400 italic py-8 text-sm">No reports configured.</div>}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// 1.9 Finance Processing Queue — approved → processed (Finance & Accounts)
// ────────────────────────────────────────────────────────────
export function FinanceProcessingQueue() {
  const [tab, setTab] = useState('approved'); // approved | processed
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const refresh = (t = tab) => apiGet('/petty-cash/requests', { status: t }).then(d => setRows(d.items || []));
  useEffect(() => { refresh(tab); /* eslint-disable-next-line */ }, [tab]);

  const process = async (id) => {
    const ref = window.prompt('Payment reference (UPI ref / cheque no / voucher) — leave blank if none:');
    if (ref === null) return; // cancelled
    setBusyId(id);
    try {
      const qs = ref ? `?payment_ref=${encodeURIComponent(ref)}` : '';
      await apiPut(`/petty-cash/requests/${id}/process${qs}`);
      toast.success('Marked as processed');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to process');
    } finally { setBusyId(null); }
  };

  return (
    <div className="p-6 space-y-4">
      <PageHeader title="Finance Processing" subtitle="Manager-approved expenses awaiting payout by Finance & Accounts" icon={<Banknote size={18} />} />
      <div className="flex items-center gap-2">
        {['approved', 'processed'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider border rounded-sm ${tab === t ? 'border-[#1F7A6E] bg-teal-50 text-[#1F7A6E]' : 'border-slate-300 text-slate-500'}`}
            data-testid={`pc-finance-tab-${t}`}>
            {t === 'approved' ? 'Awaiting Processing' : 'Processed'}
          </button>
        ))}
        <span className="text-xs text-slate-500 ml-auto">{rows.length} record(s)</span>
      </div>
      <div className="space-y-2" data-testid="pc-finance-queue">
        {rows.map(r => (
          <div key={r.id} className="bg-white border border-slate-200 rounded-sm p-4 flex flex-col md:flex-row md:items-center gap-3">
            <InvoiceThumb url={r.invoice_image_url} alt={`Bill from ${r.vendor_name || 'vendor'}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold ${KIND_PILL[r.category] || 'bg-slate-100'}`}>{r.category}</span>
                <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase border ${STATUS_BADGE[r.status] || 'bg-slate-100'}`}>{r.status}</span>
              </div>
              <div className="font-display font-bold mt-1 truncate">{r.vendor_name} · {formatINR(r.actual_amount)}</div>
              <div className="text-xs text-slate-500">{r.project_name || r.office_location_name} · submitted by {r.submitted_by_name}</div>
              <div className="text-xs text-slate-500">Approved by {r.approved_by || '—'}{r.approved_at ? ` · ${new Date(r.approved_at).toLocaleDateString('en-IN')}` : ''}</div>
              {r.invoice_number && <div className="text-[11px] text-slate-400">Bill #{r.invoice_number}</div>}
              {tab === 'processed' && (
                <div className="text-xs text-emerald-700 mt-0.5">Processed by {r.processed_by}{r.payment_ref ? ` · Ref ${r.payment_ref}` : ''}</div>
              )}
            </div>
            {tab === 'approved' && (
              <button onClick={() => process(r.id)} disabled={busyId === r.id}
                className="px-4 py-2 text-xs bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm font-display font-bold disabled:opacity-50 flex items-center gap-1.5 shrink-0"
                data-testid={`pc-process-${r.id}`}>
                {busyId === r.id ? <Loader2 size={12} className="animate-spin" /> : <Banknote size={14} />} Mark Processed
              </button>
            )}
          </div>
        ))}
        {!rows.length && <div className="text-center text-slate-400 italic py-8 text-sm">{tab === 'approved' ? 'Nothing awaiting finance processing.' : 'No processed expenses yet.'}</div>}
      </div>
    </div>
  );
}


export default PettyCashDashboard;
